import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Banknote, Coins, TrendingUp, Percent, Wallet, AlarmClock, FileStack, Landmark, Truck, AlertTriangle, Trophy, CalendarDays, Receipt, History } from "lucide-react";
import { DevredenDialog } from "@/components/dashboard/DevredenDialog";
import { api, apiError } from "@/lib/api";
import { formatNumber, formatPct, formatTRY } from "@/lib/format";
import { PageHeader } from "@/components/common/States";
import { useAuth } from "@/context/AuthContext";
import RangeFilter, { rangeParams } from "@/components/common/RangeFilter";
import { TrendChart, Widgets } from "@/components/dashboard/Widgets";
import { BackupAlertBanner } from "@/components/dashboard/BackupAlertBanner";
import BalanceCheckCard from "@/components/dashboard/BalanceCheckCard";
import VaultDueCard from "@/components/dashboard/VaultDueCard";
import { TodaySummaryCard } from "@/components/dashboard/TodaySummaryCard";

const Kpi = ({ icon: Icon, label, value, hint, testid, to, tone = "text-slate-900", delay = 0, onClick }) => {
  const nav = useNavigate();
  return (
    <button type="button" onClick={() => (onClick ? onClick() : to && nav(to))} data-testid={testid} style={{ animationDelay: `${delay}ms` }}
      className="panel p-4 text-left fade-up hover:border-blue-500 transition-[border-color,transform] duration-150 hover:-translate-y-0.5">
      <div className="flex items-center justify-between"><span className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">{label}</span><Icon className="h-4 w-4 text-slate-400" /></div>
      <div className={`mt-2 font-mono text-lg xl:text-xl font-semibold num ${tone}`} data-testid={`${testid}-value`}>{value}</div>
      {hint && <div className="text-[11px] text-slate-400 mt-1 truncate">{hint}</div>}
    </button>
  );
};

function Cards({ c, label }) {
  const [dev, setDev] = useState(false);
  const has = (k) => k in c;
  const k = [
    [Receipt, "Bugünkü Ciro", formatTRY(c.today_sales), "Faturalaşan net (KDV hariç) − iade", "kpi-today-sales", "/raporlar?r=satis&range=today", "text-blue-700"],
    [CalendarDays, "Aylık Ciro", formatTRY(c.month_sales), "Bu ay net ciro", "kpi-month-sales", "/raporlar?r=satis&range=month", "text-blue-700"],
    [Banknote, "Bugünkü Tahsilat", formatTRY(c.today_collections), null, "kpi-today-collections", "/raporlar?r=tahsilat&range=today", "text-emerald-700"],
    [Wallet, "Aylık Tahsilat", formatTRY(c.month_collections), null, "kpi-month-collections", "/raporlar?r=tahsilat&range=month", "text-emerald-700"],
    has("gross_profit") && [TrendingUp, `Brüt Kâr · ${label}`, formatTRY(c.gross_profit), `Ciro ${formatTRY(c.range_sales)}`, "kpi-gross-profit", "/raporlar?r=karlilik", "text-emerald-700"],
    has("margin_pct") && [Percent, `Kâr Marjı % · ${label}`, formatPct(c.margin_pct, 2), `${c.order_count} fatura`, "kpi-margin", "/raporlar?r=karlilik"],
    [Coins, "Toplam Alacak", formatTRY(c.receivables), null, "kpi-receivables", c.payables != null ? "/raporlar?r=alacaklar" : "/raporlar?r=cari_risk"],
    ...(c.payables != null ? [[Coins, "Toplam Borç", formatTRY(c.payables), "Net alacaklı cariler (borcumuz)", "kpi-payables", "/raporlar?r=borclar", "text-rose-700"]] : []),
    has("devreden") && [History, "Devreden Bakiye", formatTRY(c.devreden), "Açılış borçlarının kalanı · vadeye girmez", "kpi-devreden", null, "text-slate-900", () => setDev(true)],
    [AlarmClock, "Vadesi Geçmiş Alacak", formatTRY(c.overdue), `${c.overdue_customers} cari`, "kpi-overdue", "/yaklasan-tahsilat", "text-rose-600"],
    has("instrument_portfolio") && [FileStack, "Çek/Senet Portföyü", formatTRY(c.instrument_portfolio), `${c.instrument_count} evrak`, "kpi-instruments", "/cek-senet"],
    has("cash_bank") && [Landmark, "Kasa + Banka", formatTRY(c.cash_bank), `Kasa ${formatTRY(c.cash)}`, "kpi-cash-bank", "/finans"],
    [Truck, "Sevk / Fatura Bekleyen", `${formatNumber(c.proforma_count)} proforma · ${formatNumber(c.ready_to_ship)} sevke hazır`, `${c.pipeline_count} sipariş faturalaşmayı bekliyor${c.approval_pending ? ` · ${c.approval_pending} onay bekliyor` : ""}`, "kpi-shipping", "/siparisler?status=proforma"],
    [AlertTriangle, "Kritik Stok", formatNumber(c.critical_stock), "Min. stok altında", "kpi-critical-stock", "/stok", "text-amber-600"],
    has("bonus_amount") && [Trophy, "Ödenmemiş Prim", formatTRY(c.bonus_amount), "Onaylı, ödenmemiş", "kpi-unpaid-prim", "/prim-performans?tab=accounts", "text-rose-600"],
  ].filter(Boolean);
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-4" data-testid="dashboard-kpis">
      {k.map(([icon, l, v, h, t, to, tone, onClick], i) => <Kpi key={t} icon={icon} label={l} value={v} hint={h} testid={t} to={to} tone={tone} onClick={onClick} delay={i * 25} />)}
      {dev && <DevredenDialog onClose={() => setDev(false)} />}
    </div>
  );
}

import BackorderCards from "@/components/backorders/BackorderCards";
import PromiseCards from "@/components/dashboard/PromiseCards";
import DelayedContainersCard from "@/components/dashboard/DelayedContainersCard";

export default function Dashboard() {
  const { user } = useAuth();
  const [f, setF] = useState({ range: "month", start: "", end: "" });
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    const p = rangeParams(f);
    if (!p) return;
    setErr("");
    api.get("/dashboard/overview", { params: p }).then(({ data }) => setD(data)).catch((e) => setErr(apiError(e)));
  }, [f]);
  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Genel Bakış" subtitle={`Hoş geldiniz, ${user.name} · ${user.role_name}${d ? ` · ${d.range_label}: ${d.start.split("-").reverse().join(".")} – ${d.end.split("-").reverse().join(".")}` : ""}`} testid="dashboard-title" />
      <div className="mb-4"><RangeFilter value={f} onChange={setF} prefix="dash" /></div>
      <BackupAlertBanner />
      <BalanceCheckCard />
      <VaultDueCard />
      <TodaySummaryCard />
      <BackorderCards />
      <PromiseCards />
      <DelayedContainersCard />
      {err && <div className="panel p-3 text-rose-700 text-sm mb-3" data-testid="dashboard-error">{err}</div>}
      {!d ? <div className="h-40 panel animate-pulse" /> : (<>
        <Cards c={d.cards} label={d.range_label} />
        <TrendChart series={d.widgets.series} />
        <Widgets w={d.widgets} />
      </>)}
    </div>
  );
}
